import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatCircleDots, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STARTER_PROMPTS = [
  "Summarize my highest-priority emails this morning",
  "Draft a warm renewal email for a long-standing tenant",
  "What should I do about a tenant 30+ days past due?",
  "Rental trends in zip 78704 under $2500",
];

export default function Assistant() {
  const [sessionId] = useState(() => crypto.randomUUID());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const send = async (content) => {
    const text = (content ?? input).trim();
    if (!text) return;
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assistant failed"); }
    setSending(false);
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto h-full flex flex-col" data-testid="assistant-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-400">AI Assistant</div>
        <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">Chat with rentmore24</h1>
        <p className="text-stone-500 mt-1">Ask anything about leases, emails, tenants, or market trends.</p>
      </div>

      <Card className="flex-1 flex flex-col border-stone-200 shadow-none rounded-xl bg-white overflow-hidden">
        <div ref={boxRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {msgs.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-forest-50 text-forest-600 flex items-center justify-center mb-3">
                <ChatCircleDots size={30} weight="duotone" />
              </div>
              <div className="font-heading text-xl text-stone-900">How can I help?</div>
              <div className="text-sm text-stone-500 mt-1">Try one of these to get started:</div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center max-w-xl">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="px-3 py-2 rounded-full bg-sand-100 hover:bg-sand-200 text-sm text-stone-700 border border-stone-200 transition-colors"
                    data-testid={`starter-${p.slice(0, 10)}`}
                  >
                    <Sparkle size={12} weight="fill" className="inline mr-1 text-forest-500" />{p}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
              <div className={`max-w-[80%] px-4 py-3 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${
                m.role === "user" ? "bg-forest-600 text-white rounded-br-sm" : "bg-sand-100 text-stone-800 rounded-bl-sm border border-stone-200"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-sand-100 text-stone-500 text-sm px-4 py-3 rounded-2xl rounded-bl-sm border border-stone-200">
                <span className="inline-block animate-pulse">Thinking…</span>
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-4 border-t border-stone-200 bg-sand-50 flex gap-2"
          data-testid="assistant-form"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about leases, emails, tenants…"
            className="bg-white"
            data-testid="assistant-input"
          />
          <Button type="submit" disabled={sending || !input.trim()} className="bg-forest-600 hover:bg-forest-500 text-white" data-testid="assistant-send">
            <PaperPlaneRight size={16} />
          </Button>
        </form>
      </Card>
    </div>
  );
}
